import React, { useEffect, useState, useCallback } from "react";
import { ClipboardList, CheckCircle2, AlertTriangle, PackageX, Inbox, Loader2, ArrowRight, Users, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const KPI = ({ icon: Icon, label, value, tone = "forest", onClick }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={`hk-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`text-left card-lux p-4 ${onClick ? "hover:shadow-md transition-shadow cursor-pointer" : "cursor-default"}`}>
    <div className="flex items-center gap-2 text-stone-500"><Icon size={16} className={`text-${tone}`} /><span className="text-xs">{label}</span></div>
    <div className="font-serif text-3xl text-forest mt-2">{value}</div>
  </button>
);

export default function HousekeepingDashboard({ onGoTo }) {
  const [data, setData] = useState(null);
  const [reordering, setReordering] = useState(false);
  const load = useCallback(() => api.get("/admin/housekeeping/summary").then((r) => setData(r.data)).catch(() => setData({})), []);
  useEffect(() => { load(); }, [load]);
  const reorderAll = async () => {
    if (!window.confirm(`Create reorder request(s) for all ${data.low_stock_count} low-stock item(s)?`)) return;
    setReordering(true);
    try {
      const { data: res } = await api.post("/admin/housekeeping/reorder-low-stock");
      toast.success(`Reorder sent for ${res.total_items} item(s) across ${res.requests.length} request(s)`);
      const wa = (res.requests || []).find((r) => r.whatsapp_url)?.whatsapp_url;
      if (wa) window.open(wa, "_blank");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReordering(false); }
  };
  if (!data) return <Loader2 className="animate-spin text-rose mx-auto my-12" />;

  const statusCls = (s) => s === "completed" || s === "reviewed" ? "bg-green-100 text-green-700" : s === "in_progress" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500";

  return (
    <div data-testid="housekeeping-dashboard" className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-forest">Housekeeping</h2>
        <p className="text-sm text-stone-500">Today · {data.date}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI icon={ClipboardList} label="Today's cleans" value={data.today_total || 0} onClick={() => onGoTo?.("worksheets")} />
        <KPI icon={CheckCircle2} label="Completed" value={data.today_completed || 0} tone="green-600" onClick={() => onGoTo?.("worksheets")} />
        <KPI icon={AlertTriangle} label="Outstanding" value={data.today_outstanding || 0} tone="amber-600" onClick={() => onGoTo?.("worksheets")} />
        <KPI icon={PackageX} label="Low stock" value={data.low_stock_count || 0} tone="red-600" onClick={() => onGoTo?.("inventory")} />
        <KPI icon={Inbox} label="Open reorders" value={data.open_reorders || 0} tone="rose" onClick={() => onGoTo?.("reorders")} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-5" data-testid="hk-today-worksheets">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg text-forest flex items-center gap-2"><ClipboardList size={18} className="text-rose" /> Today's worksheets</h3>
            <button onClick={() => onGoTo?.("worksheets")} className="text-xs text-rose font-semibold inline-flex items-center gap-1">Open <ArrowRight size={13} /></button>
          </div>
          {(data.worksheets || []).length === 0 ? (
            <p className="text-sm text-stone-400 py-6 text-center">No cleans scheduled today.</p>
          ) : (
            <div className="space-y-2">
              {data.worksheets.map((w) => (
                <div key={w.id} className="flex items-center gap-3 bg-sand/40 rounded-lg px-3 py-2 text-sm">
                  <span className="flex-1 min-w-0"><span className="font-medium text-forest">{w.property_name || "Worksheet"}</span>{w.staff_name && <span className="text-stone-400"> · {w.staff_name}</span>}</span>
                  <span className="text-xs text-stone-500 shrink-0">{w.completion_percent || 0}%</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${statusCls(w.status)}`}>{(w.status || "").replace("_", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-lux p-5" data-testid="hk-low-stock">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg text-forest flex items-center gap-2"><PackageX size={18} className="text-red-600" /> Low-stock flags</h3>
            <div className="flex items-center gap-2">
              {(data.low_stock || []).length > 0 && (
                <button onClick={reorderAll} disabled={reordering} data-testid="hk-reorder-all" className="text-xs px-3 py-1.5 rounded-full bg-rose text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                  {reordering ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />} Reorder all
                </button>
              )}
              <button onClick={() => onGoTo?.("inventory")} className="text-xs text-rose font-semibold inline-flex items-center gap-1">Inventory <ArrowRight size={13} /></button>
            </div>
          </div>
          {(data.low_stock || []).length === 0 ? (
            <p className="text-sm text-stone-400 py-6 text-center">Everything's well stocked.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {data.low_stock.map((it, i) => (
                <div key={i} className="flex items-center gap-3 bg-red-50/60 rounded-lg px-3 py-2 text-sm">
                  <span className="flex-1 min-w-0"><span className="font-medium text-forest">{it.name}</span><span className="text-stone-400"> · {it.area}</span></span>
                  <span className="text-xs text-stone-500 shrink-0">{it.quantity} {it.unit}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 shrink-0">{it.condition}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-stone-400 flex items-center gap-1.5"><Users size={13} /> {data.active_staff || 0} active staff on the roster</div>
    </div>
  );
}
